import { useCallback, useEffect, useRef, useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { clampDimensions, calculateResizeDimensions } from './resizableImageUtils';
import type { Dimensions } from './resizableImageUtils';

const MIN_SIZE = 50;

/**
 * ResizableImage is a React node view component for TipTap that renders
 * images with drag handles for resizing.
 *
 * Features:
 * - Resize handle on bottom-right corner
 * - Maintains aspect ratio during resize
 * - Constrains dimensions: min 50px, max editor content width
 * - Stores width/height in node attributes
 * - Renders at stored dimensions by default
 *
 * Requirements: 3.4
 */
export function ResizableImage({ node, updateAttributes, editor }: NodeViewProps) {
  const { src, alt, title, width, height } = node.attrs;
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [currentDimensions, setCurrentDimensions] = useState<Dimensions>({
    width: width || 0,
    height: height || 0,
  });
  const dragStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  // Get the maximum width from the editor container
  const getMaxWidth = useCallback((): number => {
    if (containerRef.current) {
      const editorElement = containerRef.current.closest('.entry-editor-content');
      if (editorElement) {
        return editorElement.clientWidth - 32; // Account for padding
      }
    }
    return 800; // Fallback default
  }, []);

  // When image loads naturally and no stored dimensions exist, capture natural dimensions
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (!width && !height) {
      const maxWidth = getMaxWidth();
      const clamped = clampDimensions(
        { width: img.naturalWidth, height: img.naturalHeight },
        { minSize: MIN_SIZE, maxWidth }
      );
      setCurrentDimensions(clamped);
      updateAttributes({ width: clamped.width, height: clamped.height });
    }
  }, [width, height, getMaxWidth, updateAttributes]);

  // Sync state with node attributes
  useEffect(() => {
    if (width && height) {
      setCurrentDimensions({ width, height });
    }
  }, [width, height]);

  // Handle resize drag start
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!editor.isEditable) return;

    setIsResizing(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: currentDimensions.width,
      height: currentDimensions.height,
    };
  }, [editor.isEditable, currentDimensions]);

  // Handle mouse move during resize
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;

      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      const maxWidth = getMaxWidth();
      const newDimensions = calculateResizeDimensions(
        { width: dragStartRef.current.width, height: dragStartRef.current.height },
        deltaX,
        deltaY,
        { minSize: MIN_SIZE, maxWidth }
      );

      setCurrentDimensions(newDimensions);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      dragStartRef.current = null;

      // Persist final dimensions to node attributes
      updateAttributes({
        width: currentDimensions.width,
        height: currentDimensions.height,
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, currentDimensions, getMaxWidth, updateAttributes]);

  const displayWidth = currentDimensions.width || undefined;
  const displayHeight = currentDimensions.height || undefined;

  return (
    <NodeViewWrapper
      as="span"
      className="resizable-image-wrapper"
      style={{ display: 'inline-block', position: 'relative' }}
      ref={containerRef}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt || ''}
        title={title || undefined}
        width={displayWidth}
        height={displayHeight}
        onLoad={handleImageLoad}
        draggable={false}
        style={{
          display: 'block',
          maxWidth: '100%',
          borderRadius: 'var(--radius-sm, 8px)',
        }}
      />
      {editor.isEditable && (
        <span
          className="resizable-image-handle"
          role="separator"
          aria-label="Resize image"
          onMouseDown={handleResizeStart}
          style={{
            position: 'absolute',
            bottom: 4,
            right: 4,
            width: 12,
            height: 12,
            backgroundColor: 'var(--color-primary, #E8A0BF)',
            border: '2px solid white',
            borderRadius: 2,
            cursor: 'nwse-resize',
            opacity: isResizing ? 1 : 0.7,
            transition: 'opacity 150ms ease',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
          onMouseLeave={(e) => {
            if (!isResizing) {
              (e.currentTarget as HTMLElement).style.opacity = '0.7';
            }
          }}
        />
      )}
      {isResizing && (
        <span
          className="resizable-image-dimensions"
          aria-live="polite"
          style={{
            position: 'absolute',
            bottom: -20,
            right: 0,
            fontSize: 11,
            color: 'var(--color-text-secondary, #6B5B6E)',
            backgroundColor: 'var(--color-bg-card, white)',
            padding: '1px 4px',
            borderRadius: 3,
            whiteSpace: 'nowrap',
          }}
        >
          {currentDimensions.width} × {currentDimensions.height}
        </span>
      )}
    </NodeViewWrapper>
  );
}

export default ResizableImage;
